import { AppMainComponent } from './../../../../app.main.component';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import { AppMainService } from '../../../../app-main.service';
import { QuestionnaireDetailsService } from '../../../../service/questionnaire/questionnaire.service';
import { Status } from '../../../../models/statusEnums';
import { ParseHelper } from '../../../../modules/shared/parse.helper';

@Component({
    selector: 'app-questionnaire-groups',
    templateUrl: './groups.component.html',
    styleUrls: ['./groups.component.scss']
})
export class CrmQuestionnaireGroupsComponent implements OnInit, OnChanges, OnDestroy {

    protected ngUnsubscribe$: Subject<void> = new Subject<void>();

    @Input() displayGroupAdd: boolean = false;
    @Output() closeDialog = new EventEmitter();

    //Questions Group
    emptyMessage: string = "Loading...";
    tableShowMore: number[] = [15, 30, 50, 100];
    questionGroups: any[] = [];
    isActive: boolean = true;

    //Add Edit Question Group
    addeEditDialogHeader: string = "Add Question Group";
    displayQuestionGroupAddEdit: boolean = false;
    selectedQuestionGroup: any = null;
    questionnaireQuestions: any[] = [];
    formSubmitted: boolean = false;

    constructor(
        private app: AppMainComponent,
        private messageService: MessageService,
        private appMainService: AppMainService,
        private confirmationService: ConfirmationService,
        private questionnaireDetailsService: QuestionnaireDetailsService
    ) { }

    ngOnInit(): void {
        this.selectedQuestionGroup = {
            id: 0,
            name: "",
            isActive: true
        }
        this.getQuestionGroups();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if(changes['displayGroupAdd'] && changes['displayGroupAdd'].currentValue){
            this.addQuestionGroup();
        }
    }

    getQuestions(openType: 'add'|'edit' = 'add') {
        this.questionnaireDetailsService.getallQuestionnaireQuestions(true).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
            next: (response) => {
                this.questionnaireQuestions = response || [];
                this.questionnaireQuestions.map(question=>{
                    question.isValid = false;
                });

                if(openType=='edit' && this.selectedQuestionGroup?.surveyGroupQuestions?.length>0){
                    this.questionnaireQuestions.map(question=>{
                        const added = this.selectedQuestionGroup.surveyGroupQuestions.find(mapped=> mapped.id==question.id);

                        if(added){
                            question.isValid = true;
                            question.displayOrder = added.displayOrder;
                        }
                    });
                }

                this.sortQuestions();
                this.displayQuestionGroupAddEdit = true;
            },
            error: (error) => {}
        });
    }

    onRowReorder(event) {
    }

    onCheckboxChange(question: any) {
        question.isValid = question.isValid;
        this.sortQuestions();
    }

    sortQuestions() {
        this.questionnaireQuestions.sort((a, b) => {
            if (a.isValid === b.isValid) {
                if (a.displayOrder === 0 && b.displayOrder === 0) {
                    return 0;
                } else if (a.displayOrder === 0) {
                    return 1;
                } else if (b.displayOrder === 0) {
                    return -1;
                } else {
                    return a.displayOrder - b.displayOrder;
                }
            } else {
                return a.isValid ? -1 : 1;
            }
        });
    }

    getQuestionGroups(){
        this.appMainService.isShowSpinner('getQuestionGroups');
        this.questionnaireDetailsService.getQuestionGroups(this.isActive).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
            next: (response)=>{
                this.appMainService.isHideSpinner('getQuestionGroups');
                const statusId = this.isActive ? 1 : 2;
                this.questionGroups = (response||[]).filter(g=> g.statusId==statusId);
                this.emptyMessage = this.questionGroups?.length==0 ? "No questions group created yet." : "";
            },
            error: (error)=>{
                this.appMainService.isHideSpinner('getQuestionGroups');
                this.emptyMessage = "No questions group created yet.";
            }
        })
    }

    addQuestionGroup(){
        this.selectedQuestionGroup = {
            id: 0,
            name: "",
            isActive: true
        }
        this.getQuestions('add');
    }

    editQuestionGroup(questionGroup: any){
        this.selectedQuestionGroup = ParseHelper.DeepCopy(questionGroup);
        this.selectedQuestionGroup.isActive = !!(this.selectedQuestionGroup.statusId == Status.Active);
        this.getQuestions('edit')
    }

    saveQuestionGroup(){
        this.formSubmitted = true;
        const questionGroup = {
            id: this.selectedQuestionGroup?.id||0,
            name: this.selectedQuestionGroup.name,
            statusId: this.selectedQuestionGroup?.isActive ? Status.Active : Status.InActive,
            surveyGroupQuestionMappers: this.questionnaireQuestions.filter(question=> question.isValid).map(question=>{
                return {
                    questionGroupId: this.selectedQuestionGroup?.id||0,
                    questionId: question.id,
                    displayOrder: question.displayOrder
                }
            })
        }

        this.appMainService.isShowSpinner('saveQuestionGroup');

        const api = questionGroup.id>0 ? this.questionnaireDetailsService.updateQuestionGroup(questionGroup) : this.questionnaireDetailsService.addQuestionGroup(questionGroup);
        api.pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
            next: (response)=>{
                this.formSubmitted = false;
                this.appMainService.isHideSpinner('saveQuestionGroup');
                this.messageService.add({ severity: 'success', summary: 'Success', detail: `Question group ${questionGroup.id>0 ? 'updated' : 'add'} successfully...` });
                this.getQuestionGroups();
                this.cancelAddEdit();
            },
            error: (error)=>{
                this.formSubmitted = false;
                this.appMainService.isHideSpinner('saveQuestionGroup');
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.Message||`Failed to ${questionGroup.id>0 ? 'update' : 'add'} question group...` });
            }
        });
    }

    deleteQuestionGroup(questionGroup: any){
        this.confirmationService.confirm({
            message: 'Do you want to delete this Question Group?',
            header: 'Delete Confirmation',
            icon: 'pi pi-info-circle',
            accept: () => {
                this.questionnaireDetailsService.deleteQuestionGroup(questionGroup.id).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
                    next: (response)=>{
                        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Question Group Deleted Successfully...' });
                        this.getQuestionGroups();
                    },
                    error: (error)=>{
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.Message });
                    }
                });
            },
            reject: () => {
            }
        });
    }

    cancelAddEdit(){
        this.closeDialog.emit(true);
        this.displayQuestionGroupAddEdit = false;
    }

    get getSelectedQuestions(){
        return this.questionnaireQuestions.filter(question=> question.isValid).length;
    }

    ngOnDestroy(): void {
        if (this.ngUnsubscribe$) {
            this.ngUnsubscribe$.next();
            this.ngUnsubscribe$.complete();
            this.ngUnsubscribe$.unsubscribe();
        }
    }

}
